import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  ChevronLeft, ChevronRight, Calendar, Clock, CheckCircle,
  XCircle, AlertCircle, Loader, LogOut, FileText, Bell,
  PlusCircle, RefreshCw, Download, ChevronDown, Check, X
} from 'lucide-react';

/* ─── Design tokens (match Dashboard light theme) ─── */
const T = {
  bg: '#f8f7ff', surface: '#ffffff', border: '#e8e4f3', borderL: '#f0edfb',
  purple: '#7c3aed', purpleL: '#8b5cf6', purpleBg: '#ede9fe', purpleText: '#6d28d9',
  text: '#1a1035', textMd: '#4a3f6b', textSm: '#8b7fb0', textXs: '#b8b3cc',
  green: '#059669', greenBg: '#d1fae5',
  red: '#dc2626', redBg: '#fee2e2',
  yellow: '#d97706', yellowBg: '#fef3c7',
  blue: '#2563eb', blueBg: '#dbeafe',
  pink: '#db2777', pinkBg: '#fce7f3',
  orange: '#ea580c', orangeBg: '#ffedd5',
  font: "'Poppins', sans-serif",
  shadow: '0 1px 4px rgba(124,58,237,0.06)',
  shadowMd: '0 4px 16px rgba(124,58,237,0.1)',
};

/* ─── Calendar day colors ─── */
const DAY_COLORS = {
  present:      { bg: '#d1fae5', text: '#059669', dot: '#059669', label: 'Present' },
  half_day:     { bg: '#ccfbf1', text: '#0d9488', dot: '#0d9488', label: 'Half Day' },
  absent:       { bg: '#fee2e2', text: '#dc2626', dot: '#dc2626', label: 'Absent' },
  leave:        { bg: '#ede9fe', text: '#6d28d9', dot: '#7c3aed', label: 'Approved Leave' },
  permission:   { bg: '#fce7f3', text: '#db2777', dot: '#db2777', label: 'Permission' },
  holiday:      { bg: '#fef3c7', text: '#d97706', dot: '#f59e0b', label: 'Holiday' },
  weekend:      { bg: '#f9fafb', text: '#9ca3af', dot: null,      label: 'Weekend' },
  regularized:  { bg: '#dbeafe', text: '#2563eb', dot: '#2563eb', label: 'Regularized' },
  today:        { bg: '#ede9fe', text: '#7c3aed', dot: null,      label: 'Today' },
  future:       { bg: '#ffffff', text: '#1a1035', dot: null,      label: 'Upcoming' },
  pre_golive:   { bg: '#f9fafb', text: '#d1d5db', dot: null,      label: 'Pre Launch' },
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

/* ─── Helpers ─── */
const fmt = (n) => n < 10 ? `0${n}` : `${n}`;
const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const h = d.getHours(), m = d.getMinutes();
  return `${fmt(h > 12 ? h - 12 : h || 12)}:${fmt(m)} ${h >= 12 ? 'PM' : 'AM'}`;
};
const fmtDuration = (mins) => {
  if (!mins) return '—';
  const h = Math.floor(mins / 60), m = mins % 60;
  return `${h}h ${fmt(m)}m`;
};

/* ═══════════════════════════════════════
   STAT CARD
═══════════════════════════════════════ */
function StatCard({ label, value, icon: Icon, color, bg }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: T.shadow, flex: 1, minWidth: 130 }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={22} color={color} />
      </div>
      <div>
        <div style={{ fontWeight: 800, fontSize: 26, color: T.text, lineHeight: 1 }}>{value}</div>
        <div style={{ fontWeight: 500, fontSize: 13, color: T.textSm, marginTop: 4 }}>{label}</div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   ATTENDANCE CALENDAR
═══════════════════════════════════════ */
function AttendanceCalendar({ records, holidays, month, year, today }) {
  // Go-live date — no absent marking before this
  const GO_LIVE = '2026-05-01';

  const getDayStatus = (dateStr) => {
    const rec = records.find(r => r.date === dateStr);
    const isToday = dateStr === today;
    const isFuture = dateStr > today;
    const isBeforeGoLive = dateStr < GO_LIVE;
    const isHoliday = holidays.find(h => h.date === dateStr);
    const d = new Date(dateStr + 'T00:00:00');
    const isSunday = d.getDay() === 0;

    if (isSunday) return { status: 'weekend', holiday: null, rec: null };
    if (isFuture && !isHoliday) return { status: 'future', holiday: null, rec: null };
    if (isHoliday) return { status: 'holiday', holiday: isHoliday, rec };
    if (rec && rec.status === 'present') {
      // Phase 5: half day detection
      const status = rec.is_half_day ? 'half_day' : 'present';
      return { status, holiday: null, rec };
    }
    if (rec && rec.status !== 'absent') {
      return { status: rec.status, holiday: null, rec };
    }
    // Before go-live — show grey, never red absent
    if (isBeforeGoLive && !isFuture) return { status: 'pre_golive', holiday: null, rec: null };
    // After go-live — show absent
    if (!isFuture) return { status: 'absent', holiday: null, rec: null };
    return { status: 'future', holiday: null, rec: null };
  };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1).getDay(); // 0=Sun
  // Adjust so Mon=0
  const startOffset = firstDay === 0 ? 6 : firstDay - 1;
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
        {DAYS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontWeight: 600, fontSize: 12, color: d === 'Sun' ? T.red : T.textSm, padding: '4px 0' }}>{d}</div>
        ))}
      </div>
      {/* Calendar cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`}/>;
          const dateStr = `${year}-${fmt(month)}-${fmt(day)}`;
          const { status, holiday, rec } = getDayStatus(dateStr);
          const cfg = DAY_COLORS[status] || DAY_COLORS.future;
          const isToday = dateStr === today;
          const isSunday = new Date(dateStr + 'T00:00:00').getDay() === 0;

          return (
            <div key={day} title={holiday?.name || cfg.label}
              style={{
                background: cfg.bg,
                border: isToday ? `2px solid ${T.purple}` : `1px solid ${isSunday ? '#f3f4f6' : T.borderL}`,
                borderRadius: 10,
                padding: '7px 4px 6px',
                textAlign: 'center',
                cursor: 'default',
                minHeight: 52,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between',
                transition: 'all 0.15s',
              }}>
              <span style={{ fontWeight: isToday ? 800 : 500, fontSize: 13, color: isSunday ? '#9ca3af' : cfg.text, lineHeight: 1 }}>{day}</span>
              {/* Dot indicator */}
              {cfg.dot && (
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, marginTop: 3 }}/>
              )}
              {!cfg.dot && <div style={{ height: 6 }}/>}
              {/* Holiday name micro */}
              {holiday && (
                <div style={{ fontSize: 8, color: T.yellow, fontWeight: 600, lineHeight: 1, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingTop: 1 }}>
                  {holiday.name.split(' ')[0]}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16, padding: '12px 0', borderTop: `1px solid ${T.borderL}` }}>
        {[
          { dot: '#059669', label: 'Present' },
          { dot: '#0d9488', label: 'Half Day' },
          { dot: '#dc2626', label: 'Absent / LOP' },
          { dot: '#f59e0b', label: 'Holiday' },
          { dot: '#7c3aed', label: 'Approved Leave' },
          { dot: '#db2777', label: 'Permission' },
          { dot: '#2563eb', label: 'Regularized' },
        ].map(({ dot, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }}/>
            <span style={{ fontSize: 11, fontWeight: 500, color: T.textSm }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   TODAY STATUS BAR
═══════════════════════════════════════ */
function TodayBar({ todayRecord, onLogout }) {
  const [elapsed, setElapsed] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (!todayRecord?.login_time || todayRecord?.logout_time) return;
    const tick = () => {
      const diff = Math.floor((Date.now() - new Date(todayRecord.login_time)) / 1000);
      const h = Math.floor(diff / 3600), m = Math.floor((diff % 3600) / 60), s = diff % 60;
      setElapsed(`${fmt(h)}:${fmt(m)}:${fmt(s)}`);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [todayRecord?.login_time]);

  const isLoggedIn = todayRecord?.login_time && !todayRecord?.logout_time;
  const isLoggedOut = todayRecord?.logout_time;

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: T.shadow }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: T.text, marginBottom: 14 }}>Today's Status</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {/* Login time */}
        <div style={{ flex: 1, minWidth: 100, background: T.greenBg, borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <CheckCircle size={14} color={T.green}/>
            <span style={{ fontWeight: 600, fontSize: 12, color: T.green }}>Logged In</span>
          </div>
          <div style={{ fontWeight: 700, fontSize: 16, color: T.text }}>{fmtTime(todayRecord?.login_time)}</div>
        </div>
        {/* Logout time */}
        <div style={{ flex: 1, minWidth: 100, background: isLoggedOut ? '#fee2e2' : '#f9fafb', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <LogOut size={14} color={isLoggedOut ? T.red : T.textXs}/>
            <span style={{ fontWeight: 600, fontSize: 12, color: isLoggedOut ? T.red : T.textXs }}>Logged Out</span>
          </div>
          <div style={{ fontWeight: 700, fontSize: 16, color: T.text }}>{fmtTime(todayRecord?.logout_time)}</div>
        </div>
        {/* Total time */}
        <div style={{ flex: 1, minWidth: 100, background: T.purpleBg, borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <Clock size={14} color={T.purple}/>
            <span style={{ fontWeight: 600, fontSize: 12, color: T.purple }}>{isLoggedIn ? 'Live' : 'Total Time'}</span>
          </div>
          <div style={{ fontWeight: 700, fontSize: 16, color: T.text }}>
            {isLoggedIn ? elapsed || '00:00:00' : fmtDuration(todayRecord?.duration_minutes)}
          </div>
        </div>
      </div>

      {/* Logout button */}
      {isLoggedIn && (
        <div style={{ marginTop: 14 }}>
          {!showLogoutConfirm ? (
            <button onClick={() => setShowLogoutConfirm(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#fee2e2', border: '1px solid #fca5a5', color: T.red, borderRadius: 10, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>
              <LogOut size={15}/> Mark Log Out
            </button>
          ) : (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#9a3412', marginBottom: 10 }}>
                ⚠ Logging out will finalize your attendance for today. Continue?
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { onLogout(); setShowLogoutConfirm(false); }}
                  style={{ background: T.red, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>
                  Yes, Log Out
                </button>
                <button onClick={() => setShowLogoutConfirm(false)}
                  style={{ background: T.surface, color: T.textMd, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: T.font }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {!todayRecord?.login_time && (
        <div style={{ marginTop: 12, padding: '10px 14px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 13, fontWeight: 500, color: '#92400e' }}>
          You haven't logged in today. Your attendance will be marked absent.
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   ACTIVITY LOG
═══════════════════════════════════════ */
function ActivityLog({ records }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: T.shadow }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.borderL}` }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: T.text }}>Activity Log</div>
        <div style={{ fontWeight: 400, fontSize: 12, color: T.textSm, marginTop: 2 }}>Your recent login & logout history</div>
      </div>
      {/* Table header */}
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 90px 90px', padding: '10px 20px', background: '#faf9ff', borderBottom: `1px solid ${T.borderL}` }}>
        {['Date','Login','Logout','Duration','Status'].map(h => (
          <span key={h} style={{ fontWeight: 600, fontSize: 11, color: T.textSm, textTransform: 'uppercase', letterSpacing: 0.8 }}>{h}</span>
        ))}
      </div>
      <div style={{ maxHeight: 320, overflow: 'auto' }}>
        {records.length === 0 ? (
          <div style={{ padding: '28px', textAlign: 'center', color: T.textSm, fontSize: 14 }}>No activity recorded yet</div>
        ) : records.filter(r => r.login_time).slice(0, 20).map((rec, idx) => {
          const cfg = DAY_COLORS[rec.status] || DAY_COLORS.future;
          const d = new Date(rec.date);
          const dateLabel = `${d.getDate()} ${MONTHS[d.getMonth()].slice(0,3)} ${d.getFullYear()}`;
          return (
            <div key={rec.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 90px 90px', padding: '12px 20px', borderBottom: idx < records.length - 1 ? `1px solid ${T.borderL}` : 'none', alignItems: 'center' }}>
              <span style={{ fontWeight: 500, fontSize: 13, color: T.text }}>{dateLabel}</span>
              <span style={{ fontWeight: 400, fontSize: 13, color: T.textMd }}>{fmtTime(rec.login_time)}</span>
              <span style={{ fontWeight: 400, fontSize: 13, color: T.textMd }}>{fmtTime(rec.logout_time)}</span>
              <span style={{ fontWeight: 500, fontSize: 13, color: T.textMd }}>{fmtDuration(rec.duration_minutes)}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: cfg.bg, color: cfg.text, fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, width: 'fit-content' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot || cfg.text, flexShrink: 0 }}/>
                {rec.status.charAt(0).toUpperCase() + rec.status.slice(1)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   RIGHT SIDEBAR — Leave + Summary
═══════════════════════════════════════ */
function AttendanceSidebar({ quota, leaves, permissions, regularizations, monthStats, onRefresh }) {
  const [activeForm, setActiveForm] = useState(null); // 'casual'|'emergency'|'permission'|'regularization'
  const [formData, setFormData] = useState({ date: '', from_date: '', to_date: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  const submit = async () => {
    if (!formData.reason.trim()) { toast.error('Please enter a reason'); return; }
    setSubmitting(true);
    try {
      if (activeForm === 'casual') {
        if (!formData.from_date) { toast.error('Select leave date'); setSubmitting(false); return; }
        await api.post('/leaves/apply', {
          leave_type: activeForm,
          from_date: formData.from_date,
          to_date: formData.to_date || formData.from_date,
          reason: formData.reason,
        });
        toast.success('Leave applied successfully');
      } else if (activeForm === 'permission') {
        if (!formData.date) { toast.error('Select date'); setSubmitting(false); return; }
        await api.post('/leaves/permission/apply', { date: formData.date, reason: formData.reason });
        toast.success('Permission applied successfully');
      } else if (activeForm === 'regularization') {
        if (!formData.date) { toast.error('Select date'); setSubmitting(false); return; }
        await api.post('/leaves/regularization/apply', { date: formData.date, reason: formData.reason });
        toast.success('Regularization request submitted');
      }
      setActiveForm(null);
      setFormData({ date: '', from_date: '', to_date: '', reason: '' });
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit');
    }
    setSubmitting(false);
  };

  const pendingLeave = leaves.find(l => l.status === 'pending' && l.leave_type === 'casual');
  const approvedLeave = leaves.find(l => l.status === 'approved');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Apply Leave / Permission Form */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: T.shadow }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: T.text, marginBottom: 14 }}>Apply Leave / Permission</div>

        {/* Quick action buttons */}
        {!activeForm && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Casual Leave */}
            {quota.casual?.available > 0 && !pendingLeave ? (
              <button onClick={() => setActiveForm('casual')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: T.purpleBg, border: `1px solid #c4b5fd`, borderRadius: 10, padding: '11px 14px', cursor: 'pointer', fontFamily: T.font }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: T.purpleText }}>Apply Casual Leave</span>
                <span style={{ fontWeight: 600, fontSize: 11, color: T.purple, background: '#fff', padding: '2px 8px', borderRadius: 12 }}>{quota.casual.available} left</span>
              </button>
            ) : pendingLeave ? (
              <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, padding: '11px 14px' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#92400e' }}>Casual Leave Pending</div>
                <div style={{ fontWeight: 400, fontSize: 12, color: '#a16207', marginTop: 3 }}>Applied for {pendingLeave.from_date} — awaiting admin approval</div>
              </div>
            ) : (
              <div style={{ background: '#f9fafb', border: `1px solid ${T.border}`, borderRadius: 10, padding: '11px 14px' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: T.textSm }}>No Casual Leave Available</div>
                <div style={{ fontWeight: 400, fontSize: 12, color: T.textXs, marginTop: 3 }}>Monthly quota exhausted</div>
              </div>
            )}


            {/* Permission */}
            {quota.permission?.available > 0 ? (
              <button onClick={() => setActiveForm('permission')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: T.pinkBg, border: '1px solid #fbcfe8', borderRadius: 10, padding: '11px 14px', cursor: 'pointer', fontFamily: T.font }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: T.pink }}>Apply Permission (Half Day)</span>
                <span style={{ fontWeight: 600, fontSize: 11, color: T.pink, background: '#fff', padding: '2px 8px', borderRadius: 12 }}>{quota.permission.available}/2 left</span>
              </button>
            ) : (
              <div style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: 10, padding: '11px 14px' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: T.textSm }}>Permissions Exhausted</div>
                <div style={{ fontWeight: 400, fontSize: 12, color: T.textXs, marginTop: 3 }}>2/2 used this month</div>
              </div>
            )}

            {/* Regularization */}
            <button onClick={() => setActiveForm('regularization')}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.blueBg, border: '1px solid #bfdbfe', borderRadius: 10, padding: '11px 14px', cursor: 'pointer', fontFamily: T.font }}>
              <RefreshCw size={14} color={T.blue}/>
              <span style={{ fontWeight: 600, fontSize: 13, color: T.blue }}>Regularization Request</span>
            </button>
          </div>
        )}

        {/* Form */}
        {activeForm && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>
                {activeForm === 'casual' && 'Casual Leave'}
                {activeForm === 'emergency' && 'Emergency Leave'}
                {activeForm === 'permission' && 'Permission (Half Day)'}
                {activeForm === 'regularization' && 'Regularization Request'}
              </div>
              <button onClick={() => setActiveForm(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textSm }}><X size={16}/></button>
            </div>

            {/* Date fields */}
            {(activeForm === 'casual' || activeForm === 'emergency') && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                {[['from_date','From Date'],['to_date','To Date']].map(([field, label]) => (
                  <div key={field}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>{label}</label>
                    <input type="date" value={formData[field]} onChange={e => setFormData(p => ({ ...p, [field]: e.target.value }))}
                      style={{ width: '100%', border: `1.5px solid ${T.border}`, borderRadius: 9, padding: '9px 11px', fontSize: 13, fontFamily: T.font, color: T.text, background: '#faf9ff', outline: 'none' }}/>
                  </div>
                ))}
              </div>
            )}
            {(activeForm === 'permission' || activeForm === 'regularization') && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>Date</label>
                <input type="date" value={formData.date} onChange={e => setFormData(p => ({ ...p, date: e.target.value }))} max={activeForm === 'regularization' ? today : undefined}
                  style={{ width: '100%', border: `1.5px solid ${T.border}`, borderRadius: 9, padding: '9px 11px', fontSize: 13, fontFamily: T.font, color: T.text, background: '#faf9ff', outline: 'none' }}/>
              </div>
            )}

            {/* Reason */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: T.textMd, marginBottom: 5 }}>Reason</label>
              <textarea value={formData.reason} onChange={e => setFormData(p => ({ ...p, reason: e.target.value }))} placeholder="Enter reason..." rows={3}
                style={{ width: '100%', border: `1.5px solid ${T.border}`, borderRadius: 9, padding: '9px 11px', fontSize: 13, fontFamily: T.font, color: T.text, background: '#faf9ff', outline: 'none', resize: 'vertical', lineHeight: 1.6 }}/>
            </div>

            <button onClick={submit} disabled={submitting}
              style={{ width: '100%', background: T.purple, color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        )}
      </div>

      {/* Leave Summary */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: T.shadow }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: T.text, marginBottom: 14 }}>
          Leave Summary <span style={{ fontWeight: 400, fontSize: 12, color: T.textSm }}>({MONTHS[new Date().getMonth()]} {new Date().getFullYear()})</span>
        </div>
        {[
          { label: 'Total Casual Leave', value: `${quota.casual?.limit || 1}`, color: T.text },
          { label: 'Casual Leave Used', value: `${quota.casual?.used || 0}`, color: T.text },
          { label: 'Casual Leave Balance', value: `${quota.casual?.available || 0}`, color: quota.casual?.available > 0 ? T.green : T.red, bold: true },
          { label: 'Permissions Used', value: `${quota.permission?.used || 0} / ${quota.permission?.limit || 2}`, color: T.text },
          { label: 'Emergency Leave', value: `${leaves.filter(l => l.leave_type === 'emergency').length}`, color: T.text },
        ].map(({ label, value, color, bold }) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${T.borderL}` }}>
            <span style={{ fontWeight: bold ? 700 : 500, fontSize: 14, color: T.textMd }}>{label}</span>
            <span style={{ fontWeight: 700, fontSize: 14, color }}>{value}</span>
          </div>
        ))}
        {monthStats && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0' }}>
            <span style={{ fontWeight: 500, fontSize: 14, color: T.textMd }}>LOP Days</span>
            <span style={{ fontWeight: 700, fontSize: 14, color: monthStats.absentDays > 0 ? T.red : T.green }}>{monthStats.absentDays || 0}</span>
          </div>
        )}
      </div>

      {/* Recent Requests */}
      {(leaves.length > 0 || permissions.length > 0) && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: T.shadow }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: T.text, marginBottom: 12 }}>Recent Requests</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 220, overflow: 'auto' }}>
            {[
              ...leaves.slice(0, 4).map(l => ({ ...l, kind: 'leave' })),
              ...permissions.slice(0, 3).map(p => ({ ...p, kind: 'permission' })),
            ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 6).map(item => {
              const statusCfg = {
                pending:  { bg: '#fef3c7', color: '#92400e', text: 'Pending' },
                approved: { bg: T.greenBg,  color: T.green,   text: 'Approved' },
                rejected: { bg: T.redBg,    color: T.red,     text: 'Rejected' },
              }[item.status];
              return (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: '#faf9ff', borderRadius: 9, border: `1px solid ${T.borderL}` }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 12, color: T.text }}>
                      {item.kind === 'leave' ? `${item.leave_type.charAt(0).toUpperCase() + item.leave_type.slice(1)} Leave` : 'Permission (Half Day)'}
                    </div>
                    <div style={{ fontWeight: 400, fontSize: 11, color: T.textSm, marginTop: 1 }}>{item.from_date || item.date}</div>
                  </div>
                  <span style={{ background: statusCfg.bg, color: statusCfg.color, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
                    {statusCfg.text}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   PHASE 6 — EMPLOYEE REPORT CARD
═══════════════════════════════════════ */
function ReportCard({ monthData, activityLog, month, year }) {
  const { records = [], stats = {} } = monthData;

  // ── Streak calculator ──────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const sortedRecords = [...records]
    .filter(r => r.date <= today && r.status === 'present')
    .sort((a, b) => b.date.localeCompare(a.date));

  let streak = 0;
  let checkDate = new Date(today);
  for (let i = 0; i < 90; i++) {
    const ds = checkDate.toISOString().split('T')[0];
    const day = checkDate.getDay();
    // Skip sundays for streak
    if (day === 0) { checkDate.setDate(checkDate.getDate() - 1); continue; }
    const rec = records.find(r => r.date === ds);
    if (rec && rec.status === 'present') { streak++; }
    else if (ds <= today) { break; }
    checkDate.setDate(checkDate.getDate() - 1);
  }

  // ── Attendance % ─────────────────────────────────────────────
  const totalWorking = stats.workingDays || 0;
  const present = stats.presentDays || 0;
  const attendancePct = totalWorking > 0 ? Math.round((present / totalWorking) * 100) : 0;

  // ── Avg login time ────────────────────────────────────────────
  const loginTimes = records.filter(r => r.login_time).map(r => {
    const d = new Date(r.login_time);
    return d.getHours() * 60 + d.getMinutes();
  });
  const avgLoginMins = loginTimes.length > 0
    ? Math.round(loginTimes.reduce((a, b) => a + b, 0) / loginTimes.length)
    : null;
  const avgLoginStr = avgLoginMins != null
    ? (() => {
        const h = Math.floor(avgLoginMins / 60);
        const m = avgLoginMins % 60;
        const ampm = h >= 12 ? 'PM' : 'AM';
        return `${fmt(h > 12 ? h - 12 : h || 12)}:${fmt(m)} ${ampm}`;
      })()
    : '—';

  // ── Avg session duration ──────────────────────────────────────
  const durations = records.filter(r => r.duration_minutes > 0).map(r => r.duration_minutes);
  const avgDurMins = durations.length > 0
    ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
    : 0;

  // ── Half days ─────────────────────────────────────────────────
  const halfDays = records.filter(r => r.is_half_day && r.status === 'present').length;

  // ── Streak badge color ────────────────────────────────────────
  const streakColor = streak >= 20 ? '#059669' : streak >= 10 ? '#7c3aed' : streak >= 5 ? '#d97706' : '#6b7280';
  const streakBg    = streak >= 20 ? '#d1fae5' : streak >= 10 ? '#ede9fe' : streak >= 5 ? '#fef3c7' : '#f3f4f6';

  // ── Attendance % ring ─────────────────────────────────────────
  const radius = 44, circ = 2 * Math.PI * radius;
  const dash = (attendancePct / 100) * circ;
  const ringColor = attendancePct >= 90 ? '#059669' : attendancePct >= 75 ? '#7c3aed' : attendancePct >= 50 ? '#d97706' : '#dc2626';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Top row — big stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>

        {/* Attendance % with ring */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px', boxShadow: T.shadow, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative' }}>
            <svg width={100} height={100} style={{ transform: 'rotate(-90deg)' }}>
              <circle cx={50} cy={50} r={radius} fill="none" stroke="#ede9fe" strokeWidth={8}/>
              <circle cx={50} cy={50} r={radius} fill="none" stroke={ringColor} strokeWidth={8}
                strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.8s ease' }}/>
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontWeight: 900, fontSize: 20, color: T.text, lineHeight: 1 }}>{attendancePct}%</span>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>Attendance Rate</div>
            <div style={{ fontWeight: 400, fontSize: 12, color: T.textSm, marginTop: 2 }}>{present} of {totalWorking} working days</div>
          </div>
        </div>

        {/* Streak */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px', boxShadow: T.shadow, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <div style={{ fontSize: 40, lineHeight: 1 }}>🔥</div>
          <div style={{ fontWeight: 900, fontSize: 36, color: streakColor, lineHeight: 1 }}>{streak}</div>
          <div style={{ background: streakBg, color: streakColor, fontWeight: 700, fontSize: 11, padding: '3px 12px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: 1 }}>Day Streak</div>
          <div style={{ fontWeight: 400, fontSize: 12, color: T.textSm, textAlign: 'center' }}>
            {streak >= 20 ? 'Outstanding! 🌟' : streak >= 10 ? 'Keep it up! 💪' : streak >= 5 ? 'Good going! 👍' : 'Build your streak!'}
          </div>
        </div>

        {/* Avg Login */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px', boxShadow: T.shadow, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: T.purpleBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Clock size={22} color={T.purple}/>
          </div>
          <div style={{ fontWeight: 800, fontSize: 22, color: T.text }}>{avgLoginStr}</div>
          <div style={{ fontWeight: 500, fontSize: 13, color: T.textSm }}>Avg Login Time</div>
        </div>

        {/* Avg Session */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px', boxShadow: T.shadow, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle size={22} color="#059669"/>
          </div>
          <div style={{ fontWeight: 800, fontSize: 22, color: T.text }}>{fmtDuration(avgDurMins)}</div>
          <div style={{ fontWeight: 500, fontSize: 13, color: T.textSm }}>Avg Session</div>
        </div>
      </div>

      {/* Second row — breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Monthly Summary */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px', boxShadow: T.shadow }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: T.text, marginBottom: 16 }}>
            {MONTHS[month - 1]} {year} — Summary
          </div>
          {[
            { label: 'Total Working Days', value: stats.workingDays || 0, color: T.text },
            { label: 'Days Present', value: stats.presentDays || 0, color: T.green },
            { label: 'Half Days', value: halfDays, color: '#0d9488' },
            { label: 'Absent (LOP)', value: stats.absentDays || 0, color: T.red },
            { label: 'Approved Leave', value: stats.leaveDays || 0, color: T.purple },
            { label: 'Permissions', value: stats.permissionDays || 0, color: '#db2777' },
            { label: 'Holidays', value: stats.holidayDays || 0, color: '#d97706' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${T.borderL}` }}>
              <span style={{ fontWeight: 500, fontSize: 14, color: T.textMd }}>{label}</span>
              <span style={{ fontWeight: 700, fontSize: 14, color }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Attendance bar chart (visual breakdown) */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px', boxShadow: T.shadow }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: T.text, marginBottom: 16 }}>Visual Breakdown</div>
          {[
            { label: 'Present', value: stats.presentDays || 0, total: stats.workingDays || 1, color: '#059669', bg: '#d1fae5' },
            { label: 'Half Day', value: halfDays, total: stats.workingDays || 1, color: '#0d9488', bg: '#ccfbf1' },
            { label: 'Absent', value: stats.absentDays || 0, total: stats.workingDays || 1, color: '#dc2626', bg: '#fee2e2' },
            { label: 'Leave', value: stats.leaveDays || 0, total: stats.workingDays || 1, color: '#7c3aed', bg: '#ede9fe' },
            { label: 'Holiday', value: stats.holidayDays || 0, total: stats.workingDays || 1, color: '#d97706', bg: '#fef3c7' },
          ].map(({ label, value, total, color, bg }) => {
            const pct = total > 0 ? Math.round((value / total) * 100) : 0;
            return (
              <div key={label} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ fontWeight: 500, fontSize: 13, color: T.textMd }}>{label}</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color }}>{value} days ({pct}%)</span>
                </div>
                <div style={{ height: 8, background: '#f0edfb', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 10, transition: 'width 0.8s ease' }}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Streak history — last 30 days dots */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px', boxShadow: T.shadow }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: T.text, marginBottom: 4 }}>Last 30 Days Activity</div>
        <div style={{ fontWeight: 400, fontSize: 12, color: T.textSm, marginBottom: 16 }}>Each dot = 1 day</div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {Array.from({ length: 30 }, (_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - (29 - i));
            const ds = d.toISOString().split('T')[0];
            const rec = records.find(r => r.date === ds);
            const isSun = d.getDay() === 0;
            let dotColor = '#e5e7eb'; // future/no data
            if (isSun) dotColor = '#f3f4f6';
            else if (rec?.status === 'present' && rec?.is_half_day) dotColor = '#0d9488';
            else if (rec?.status === 'present') dotColor = '#059669';
            else if (rec?.status === 'absent') dotColor = '#dc2626';
            else if (rec?.status === 'leave') dotColor = '#7c3aed';
            else if (rec?.status === 'holiday') dotColor = '#f59e0b';
            else if (rec?.status === 'permission') dotColor = '#db2777';
            return (
              <div key={ds} title={`${ds}: ${rec?.status || (isSun ? 'Sunday' : 'No data')}`}
                style={{ width: 18, height: 18, borderRadius: 4, background: dotColor, cursor: 'default', transition: 'background 0.2s', flexShrink: 0 }}/>
            );
          })}
        </div>
        {/* Dot legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.borderL}` }}>
          {[['#059669','Present'],['#0d9488','Half Day'],['#dc2626','Absent'],['#7c3aed','Leave'],['#db2777','Permission'],['#f59e0b','Holiday']].map(([c,l])=>(
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: c }}/>
              <span style={{ fontSize: 11, fontWeight: 500, color: T.textSm }}>{l}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════
   PHASE 5 — MY WORK TAB (for editors)
   Shows today's tasks + monthly completed/rework
   Rework counted separately from completed
═══════════════════════════════════════ */
function EditorWorkTab({userId}) {
  const [allTasks,setAllTasks]=useState([]);
  const [monthReport,setMonthReport]=useState(null);
  const [loading,setLoading]=useState(true);
  const [month,setMonth]=useState(new Date().getMonth()+1);
  const [year,setYear]=useState(new Date().getFullYear());
  const today=new Date().toISOString().split('T')[0];

  const load=useCallback(async()=>{
    if(!userId) return;
    setLoading(true);
    try{
      const [tasksRes,reportRes]=await Promise.all([
        api.get('/tasks'),
        api.get(`/tasks/reports/editor/${userId}`,{params:{month,year}}),
      ]);
      setAllTasks(tasksRes.data||[]);
      setMonthReport(reportRes.data);
    }catch{toast.error('Failed to load work data');}
    finally{setLoading(false);}
  },[userId,month,year]);

  useEffect(()=>{load();},[load]);

  const todayTasks=(allTasks||[]).filter(t=>t.due_date===today||['review','rework','client_rework'].includes(t.status));

  const STATUS_CFG={
    ready_to_edit:{bg:T.greenBg,c:T.green,l:'Ready to Edit'},
    review:{bg:T.purpleBg,c:T.purple,l:'In Review'},
    rework:{bg:T.redBg,c:T.red,l:'Rework Needed'},
    client_rework:{bg:T.orangeBg,c:T.orange,l:'Client Rework'},
    client_approval:{bg:T.blueBg,c:T.blue,l:'Client Approval'},
    client_approved:{bg:T.greenBg,c:T.green,l:'Approved ✓'},
    uploaded:{bg:'#f0fdf4',c:'#16a34a',l:'Uploaded'},
  };

  const fmtD=d=>{if(!d)return'—';const dt=new Date(d+'T00:00:00');return`${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${dt.getFullYear()}`;};
  const isOver=d=>d&&d<today;

  if(loading) return <div style={{display:'flex',justifyContent:'center',padding:48}}><div style={{width:28,height:28,border:'3px solid #ede9fe',borderTop:`3px solid ${T.purple}`,borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/></div>;

  return(
    <div>
      {/* Stats cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:14,marginBottom:20}}>
        {[
          {l:'Total Tasks',v:monthReport?.total||0,c:T.purple,bg:T.purpleBg},
          {l:'Completed',v:monthReport?.completed||0,c:T.green,bg:T.greenBg},
          {l:'Rework Received',v:monthReport?.rework_count||0,c:T.red,bg:T.redBg},
          {l:'Pending',v:monthReport?.pending||0,c:T.yellow,bg:T.yellowBg},
          {l:'Overdue',v:monthReport?.overdue||0,c:T.orange,bg:T.orangeBg},
        ].map(({l,v,c,bg})=>(
          <div key={l} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:'14px 16px',boxShadow:T.shadow}}>
            <div style={{fontWeight:800,fontSize:22,color:c,lineHeight:1}}>{v}</div>
            <div style={{fontWeight:500,fontSize:11,color:T.textSm,marginTop:4}}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{background:T.yellowBg,border:'1px solid #fcd34d',borderRadius:10,padding:'9px 14px',marginBottom:16}}>
        <p style={{fontSize:11,color:'#92400e',fontWeight:500,margin:0}}>📌 Rework videos are tracked separately and NOT counted as completed. Only client-approved or uploaded videos count as your completed work.</p>
      </div>

      {/* Month nav */}
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
        <div style={{display:'flex',alignItems:'center',gap:6,background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:'7px 12px'}}>
          <button onClick={()=>{if(month===1){setMonth(12);setYear(y=>y-1);}else setMonth(m=>m-1);}} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronLeft size={14}/></button>
          <span style={{fontWeight:600,fontSize:12,color:T.text,minWidth:100,textAlign:'center'}}>{MONTHS[month-1]} {year}</span>
          <button onClick={()=>{if(month===12){setMonth(1);setYear(y=>y+1);}else setMonth(m=>m+1);}} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm,display:'flex'}}><ChevronRight size={14}/></button>
        </div>
        <button onClick={load} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:'7px 12px',cursor:'pointer',fontFamily:T.font,fontSize:12,color:T.textSm,display:'flex',alignItems:'center',gap:5}}><RefreshCw size={13}/>Refresh</button>
      </div>

      {/* Today urgent */}
      {todayTasks.length>0&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow,marginBottom:16}}>
          <div style={{padding:'10px 16px',borderBottom:`1px solid ${T.borderL}`,background:'#fff7ed',display:'flex',alignItems:'center',gap:7}}>
            <AlertCircle size={13} color={T.orange}/>
            <span style={{fontWeight:700,fontSize:12,color:'#9a3412'}}>Urgent / Due Today ({todayTasks.length})</span>
          </div>
          {todayTasks.map((t,idx)=>{
            const cfg=STATUS_CFG[t.status]||STATUS_CFG.ready_to_edit;
            const latestRework=(t.rework_notes||[]).find(n=>n.rework_type==='internal');
            return(
              <div key={t.id} style={{padding:'11px 16px',borderBottom:idx<todayTasks.length-1?`1px solid ${T.borderL}`:'none',background:t.status==='rework'||t.status==='client_rework'?'#fff5f5':T.surface}}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:13,color:T.text}}>{t.content_description}</div>
                    <div style={{fontSize:10,color:T.textXs,marginTop:2}}>{t.client_name} · {t.task_code}</div>
                    {latestRework&&(
                      <div style={{marginTop:6,background:'#fee2e2',borderRadius:7,padding:'6px 10px'}}>
                        <div style={{fontSize:10,fontWeight:700,color:T.red,marginBottom:2}}>🔄 Rework Instruction:</div>
                        <div style={{fontSize:11,color:T.textMd,lineHeight:1.5}}>{latestRework.notes}</div>
                      </div>
                    )}
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4,flexShrink:0}}>
                    <span style={{background:cfg.bg,color:cfg.c,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20}}>{cfg.l}</span>
                    {t.due_date&&<span style={{fontSize:10,color:isOver(t.due_date)?T.red:T.textSm}}>{fmtD(t.due_date)}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Completed */}
      {monthReport?.completed_tasks?.length>0&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow,marginBottom:16}}>
          <div style={{padding:'10px 16px',borderBottom:`1px solid ${T.borderL}`,background:T.greenBg}}>
            <span style={{fontWeight:700,fontSize:12,color:T.green}}>✅ Completed This Month ({monthReport.completed_tasks.length})</span>
          </div>
          {monthReport.completed_tasks.map((t,idx)=>(
            <div key={t.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 16px',borderBottom:idx<monthReport.completed_tasks.length-1?`1px solid ${T.borderL}`:'none'}}>
              <div style={{width:6,height:6,borderRadius:'50%',background:T.green,flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:12,color:T.text,fontWeight:500}}>{t.content_description}</div>
                <div style={{fontSize:10,color:T.textXs}}>{t.client_name}</div>
              </div>
              {t.rework_count>0&&<span style={{background:T.redBg,color:T.red,fontSize:9,fontWeight:600,padding:'1px 6px',borderRadius:8}}>Rework ×{t.rework_count}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Pending */}
      {monthReport?.pending_tasks?.length>0&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',boxShadow:T.shadow}}>
          <div style={{padding:'10px 16px',borderBottom:`1px solid ${T.borderL}`,background:T.yellowBg}}>
            <span style={{fontWeight:700,fontSize:12,color:T.yellow}}>⏳ Pending ({monthReport.pending_tasks.length})</span>
          </div>
          {monthReport.pending_tasks.map((t,idx)=>{
            const cfg=STATUS_CFG[t.status]||STATUS_CFG.ready_to_edit;
            return(
              <div key={t.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 16px',borderBottom:idx<monthReport.pending_tasks.length-1?`1px solid ${T.borderL}`:'none'}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,color:T.text,fontWeight:500}}>{t.content_description}</div>
                  <div style={{fontSize:10,color:T.textXs}}>{t.client_name}</div>
                </div>
                <span style={{background:cfg.bg,color:cfg.c,fontSize:9,fontWeight:600,padding:'2px 7px',borderRadius:10}}>{cfg.l}</span>
                {t.due_date&&<span style={{fontSize:10,color:isOver(t.due_date)?T.red:T.textSm,fontWeight:isOver(t.due_date)?700:400}}>{fmtD(t.due_date)}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN ATTENDANCE PAGE
═══════════════════════════════════════ */
export default function AttendancePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthData, setMonthData] = useState({ records: [], holidays: [], stats: {} });
  const [todayRecord, setTodayRecord] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [quota, setQuota] = useState({ casual: {}, permission: {} });
  const [leaves, setLeaves] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [regularizations, setRegularizations] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [monthRes, todayRes, quotaRes, leavesRes, permsRes, actRes] = await Promise.all([
        api.get('/attendance/monthly', { params: { month, year } }),
        api.get('/attendance/today'),
        api.get('/leaves/quota'),
        api.get('/leaves/my-leaves'),
        api.get('/leaves/my-permissions'),
        api.get('/attendance/activity'),
      ]);
      setMonthData(monthRes.data);
      setTodayRecord(todayRes.data);
      setQuota(quotaRes.data);
      setLeaves(leavesRes.data);
      setPermissions(permsRes.data);
      setActivityLog(actRes.data);
    } catch (err) {
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleLogout = async () => {
    try {
      const res = await api.post('/attendance/logout');
      setTodayRecord(res.data.record);
      toast.success(`Logged out. Session: ${fmtDuration(res.data.duration_minutes)}`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Logout failed');
    }
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    const now = new Date();
    if (year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1)) return;
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const { stats } = monthData;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, flexDirection: 'column', gap: 14 }}>
        <div style={{ width: 32, height: 32, border: `3px solid #ede9fe`, borderTop: `3px solid ${T.purple}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
        <span style={{ fontWeight: 500, fontSize: 14, color: T.textSm }}>Loading attendance...</span>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: T.font }}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontWeight: 800, fontSize: 26, color: T.text, letterSpacing: -0.5, marginBottom: 4 }}>Attendance</h1>
          <p style={{ fontWeight: 400, fontSize: 15, color: T.textSm }}>Track your work days, leaves and permissions</p>
        </div>
        {/* Month navigator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '8px 14px', boxShadow: T.shadow }}>
          <button onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textSm, display: 'flex', alignItems: 'center', padding: 2 }}><ChevronLeft size={16}/></button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Calendar size={15} color={T.purple}/>
            <span style={{ fontWeight: 600, fontSize: 14, color: T.text, minWidth: 110, textAlign: 'center' }}>{MONTHS[month - 1]} {year}</span>
          </div>
          <button onClick={nextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textSm, display: 'flex', alignItems: 'center', padding: 2 }}><ChevronRight size={16}/></button>
        </div>
      </div>

      {/* Tabs — Phase 6 adds Reports tab */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `2px solid ${T.borderL}`, marginBottom: 24 }}>
        {[{ id: 'overview', label: 'Overview' }, { id: 'activity', label: 'Activity Log' }, { id: 'requests', label: 'My Requests' }, { id: 'reports', label: '📊 My Report' }, { id: 'mywork', label: '🎬 My Work' }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ background: 'none', border: 'none', padding: '10px 20px', fontSize: 14, fontWeight: activeTab === tab.id ? 700 : 500, color: activeTab === tab.id ? T.purple : T.textSm, cursor: 'pointer', fontFamily: T.font, borderBottom: activeTab === tab.id ? `2px solid ${T.purple}` : '2px solid transparent', marginBottom: -2, transition: 'all 0.15s' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>
          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Stats row — Phase 5 includes Half Day card */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <StatCard label="Working Days" value={stats.workingDays || 0} icon={CheckCircle} color="#059669" bg="#d1fae5"/>
              <StatCard label="Present Days" value={stats.presentDays || 0} icon={Clock} color={T.purple} bg={T.purpleBg}/>
              <StatCard label="Half Days" value={monthData.records?.filter(r=>r.is_half_day&&r.status==='present').length || 0} icon={Clock} color="#0d9488" bg="#ccfbf1"/>
              <StatCard label="Absent / LOP" value={stats.absentDays || 0} icon={XCircle} color={T.red} bg={T.redBg}/>
              <StatCard label="Holidays" value={stats.holidayDays || 0} icon={Calendar} color="#d97706" bg="#fef3c7"/>
            </div>

            {/* Today Status */}
            <TodayBar todayRecord={todayRecord} onLogout={handleLogout}/>

            {/* Calendar */}
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px', boxShadow: T.shadow }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 16 }}>
                Attendance Calendar — {MONTHS[month - 1]} {year}
              </div>
              <AttendanceCalendar
                records={monthData.records}
                holidays={monthData.holidays}
                month={month}
                year={year}
                today={today}
              />
            </div>
          </div>

          {/* Right sidebar */}
          <AttendanceSidebar
            quota={quota}
            leaves={leaves}
            permissions={permissions}
            regularizations={regularizations}
            monthStats={stats}
            onRefresh={loadData}
          />
        </div>
      )}

      {/* ACTIVITY LOG TAB */}
      {activeTab === 'activity' && (
        <ActivityLog records={activityLog}/>
      )}

      {/* REPORTS TAB — Phase 6 */}
      {activeTab === 'reports' && (
        <ReportCard monthData={monthData} activityLog={activityLog} month={month} year={year}/>
      )}

      {/* MY WORK TAB — Phase 5 — editor task tracking */}
      {activeTab === 'mywork' && (
        <EditorWorkTab userId={user?.id}/>
      )}

      {/* REQUESTS TAB */}
      {activeTab === 'requests' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, alignItems: 'start' }}>
          {/* Leaves */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: T.shadow }}>
            <div style={{ padding: '16px 18px', borderBottom: `1px solid ${T.borderL}`, background: T.purpleBg }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: T.purpleText }}>Leave Requests</div>
            </div>
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              {leaves.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: T.textSm, fontSize: 13 }}>No leave requests</div>
              ) : leaves.map(l => (
                <div key={l.id} style={{ padding: '13px 18px', borderBottom: `1px solid ${T.borderL}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: T.text, textTransform: 'capitalize' }}>{l.leave_type} Leave</span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: l.status==='approved'?T.greenBg:l.status==='rejected'?T.redBg:'#fef3c7', color: l.status==='approved'?T.green:l.status==='rejected'?T.red:'#92400e' }}>{l.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: T.textSm }}>{l.from_date}{l.to_date !== l.from_date ? ` → ${l.to_date}` : ''}</div>
                  <div style={{ fontSize: 12, color: T.textXs, marginTop: 3 }}>{l.reason}</div>
                  {l.admin_note && <div style={{ fontSize: 11, color: T.textSm, marginTop: 4, fontStyle: 'italic' }}>Admin: {l.admin_note}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Permissions */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: T.shadow }}>
            <div style={{ padding: '16px 18px', borderBottom: `1px solid ${T.borderL}`, background: T.pinkBg }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: T.pink }}>Permission Requests</div>
            </div>
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              {permissions.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: T.textSm, fontSize: 13 }}>No permission requests</div>
              ) : permissions.map(p => (
                <div key={p.id} style={{ padding: '13px 18px', borderBottom: `1px solid ${T.borderL}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: T.text }}>Half Day</span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: p.status==='approved'?T.greenBg:p.status==='rejected'?T.redBg:'#fef3c7', color: p.status==='approved'?T.green:p.status==='rejected'?T.red:'#92400e' }}>{p.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: T.textSm }}>{p.date}</div>
                  <div style={{ fontSize: 12, color: T.textXs, marginTop: 3 }}>{p.reason}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Regularizations */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: T.shadow }}>
            <div style={{ padding: '16px 18px', borderBottom: `1px solid ${T.borderL}`, background: T.blueBg }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: T.blue }}>Regularization</div>
            </div>
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              {regularizations.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: T.textSm, fontSize: 13 }}>No regularization requests</div>
              ) : regularizations.map(r => (
                <div key={r.id} style={{ padding: '13px 18px', borderBottom: `1px solid ${T.borderL}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: T.text }}>Regularization</span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: r.status==='approved'?T.greenBg:r.status==='rejected'?T.redBg:'#fef3c7', color: r.status==='approved'?T.green:r.status==='rejected'?T.red:'#92400e' }}>{r.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: T.textSm }}>{r.date}</div>
                  <div style={{ fontSize: 12, color: T.textXs, marginTop: 3 }}>{r.reason}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}